import React, { useState, useEffect } from 'react';
import { MessageSquare, Plus, MoreVertical, Trash, Edit, X, Check, Loader, ChevronDown, ChevronRight } from 'lucide-react';
import { Chat, Theme } from '../types';
import { fetchPromptCards, fetchPromptGroups } from './firestoreFunctions';
import { useConfirmDialog } from '../providers/DialogProvider';

// Custom confirm function to avoid ESLint no-restricted-globals error
const customConfirm = (message: string): boolean => {
  // This avoids using the global confirm function directly
  return window && window.confirm ? window.confirm(message) : false;
};

// Update the PromptCard type to include new fields
interface PromptCard {
  id: string;
  title: string;
  description: string;
  query: string;
  createdBy: string;
  createdAt: Date;
  workgroupId: string | null;
  isPersonal?: boolean;
}

// Update the PromptGroup type to include new fields
interface PromptGroup {
  id: string;
  name: string;
  cards: string[];
  createdBy: string;
  createdAt: Date;
  workgroupId: string | null;
  isDefault: boolean;
  isPersonal?: boolean;
  isHeader?: boolean;
}

interface SidebarProps {
  chats: Chat[];
  selectedChat: string | null;
  onSelectChat: (chatId: string) => void;
  onNewChat: () => void;
  onDeleteChat: (chatId: string) => void;
  onRenameChat: (chatId: string, newName: string) => void;
  onPromptSelect: (prompt: string) => void;
  theme: Theme;
  user_id: string;
  workgroupId?: string;
  userName?: string;
  userEmail?: string;
  onLogout?: () => void;
  goToAdminPage?: () => void;
  goToIntegrations?: () => void;
  goToChallenges?: () => void;
}

export function Sidebar({ 
  chats, 
  selectedChat, 
  onSelectChat, 
  onNewChat, 
  onDeleteChat, 
  onRenameChat,
  onPromptSelect,
  theme, 
  user_id,
  workgroupId = 'aws',
  userName = "", 
  userEmail = "", 
  onLogout,
  goToAdminPage,
  goToIntegrations,
  goToChallenges
}: SidebarProps) {
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [newName, setNewName] = useState<string>('');
  const [loadingChatId, setLoadingChatId] = useState<string | null>(null);
  const [deletingChatId, setDeletingChatId] = useState<string | null>(null);
  const [renamingLoadingId, setRenamingLoadingId] = useState<string | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'conversations' | 'prompts'>('prompts');
  const [promptGroups, setPromptGroups] = useState<PromptGroup[]>([]);
  const [promptCards, setPromptCards] = useState<PromptCard[]>([]);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [lastRefresh, setLastRefresh] = useState<number>(Date.now());
  const { confirm } = useConfirmDialog();

  // Deduplicate chats by session_id to avoid duplicate React keys and entries
  const uniqueChats = React.useMemo(() => {
    const seen = new Set<string>();
    const result: Chat[] = [] as any;
    for (const c of chats || []) {
      const id = (c as any)["session_id"] || (c as any)["id"];
      if (!id) continue;
      if (seen.has(id)) continue;
      seen.add(id);
      result.push(c);
    }
    return result;
  }, [chats]);

  // Ensure prompt groups array has unique composite keys across personal/workgroup/header
  const uniquePromptGroups = React.useMemo(() => {
    const seen = new Set<string>();
    const result: PromptGroup[] = [] as any;
    for (const g of promptGroups || []) {
      const key = `${g.id}-${g.isPersonal ? 'p' : 'w'}-${g.isHeader ? 'h' : 'g'}-${g.workgroupId ?? 'none'}`;
      if (seen.has(key)) continue;
      seen.add(key);
      result.push(g);
    }
    return result;
  }, [promptGroups]);

  // Function to trigger a refresh
  const refreshPrompts = () => {
    setLastRefresh(Date.now());
  };

  // Add event listener for prompt card creation
  useEffect(() => {
    const handlePromptCreated = () => {
      refreshPrompts();
    };

    window.addEventListener('prompt-card-created', handlePromptCreated);
    return () => {
      window.removeEventListener('prompt-card-created', handlePromptCreated);
    };
  }, []);

  const handleMenuToggle = (e: React.MouseEvent, chatId: string) => {
    e.stopPropagation();
    setMenuOpenId(menuOpenId === chatId ? null : chatId);
    
    // If opening the menu, set a fallback timeout to close it
    if (menuOpenId !== chatId) {
      // Close menu after 5 seconds if user doesn't interact with it
      setTimeout(() => {
        setMenuOpenId((current) => current === chatId ? null : current);
      }, 5000);
    }
  };

  const handleDeleteClick = async (e: React.MouseEvent, chatId: string) => {
    e.stopPropagation();
    setMenuOpenId(null);
    // Using our custom confirm function to avoid ESLint error
    if (customConfirm('Are you sure you want to delete this conversation?')) {
      setDeletingChatId(chatId);
      try {
        await onDeleteChat(chatId);
      } finally {
        setDeletingChatId(null);
      }
    }
  };

  const handleRenameClick = (e: React.MouseEvent, chatId: string, currentName: string) => {
    e.stopPropagation();
    setMenuOpenId(null);
    setRenamingId(chatId);
    setNewName(currentName);
  };

  const submitRename = async (e: React.MouseEvent | React.KeyboardEvent, chatId: string) => {
    e.stopPropagation();
    if (newName.trim()) {
      setRenamingLoadingId(chatId);
      try {
        await onRenameChat(chatId, newName);
      } finally {
        setRenamingLoadingId(null);
        setRenamingId(null);
      }
    } else {
      setRenamingId(null);
    }
  };

  const cancelRename = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setRenamingId(null);
  };
  
  const handleKeyDown = (e: React.KeyboardEvent, chatId: string) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      submitRename(e, chatId);
    } else if (e.key === 'Escape') {
      cancelRename(e);
    }
  };
  
  // Add document click handler to close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element;
      
      // Close chat menu if open and clicked outside
      if (menuOpenId !== null && !target.closest('.menu-popup') && !target.closest('.menu-toggle')) {
        setMenuOpenId(null);
      }
      
      // Close user menu if open and clicked outside
      if (userMenuOpen && !target.closest('.user-menu-popup') && !target.closest('.user-menu-toggle')) {
        setUserMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpenId, userMenuOpen]);
  
  const handleSelectChat = async (chatId: string) => {
    if (chatId === selectedChat) return; // Don't reload if already selected
    
    setLoadingChatId(chatId);
    try {
      await onSelectChat(chatId);
    } finally {
      setLoadingChatId(null);
    }
  };

  // Handle user profile menu toggle
  const handleUserMenuToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setUserMenuOpen(!userMenuOpen);
  };

  // Handle logout
  const handleLogout = (e: React.MouseEvent) => {
    e.stopPropagation();
    setUserMenuOpen(false);
    if (onLogout) {
      onLogout();
    }
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!userName) return "U";
    
    const nameParts = userName.split(" ");
    if (nameParts.length > 1) {
      return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`;
    }
    return userName.substring(0, 2).toUpperCase();
  };

  // Fetch prompts data when in prompts view or when lastRefresh changes
  useEffect(() => {
    if (viewMode === 'prompts') {
      const loadPrompts = async () => {
        try {
          // Fetch personal prompts (workgroupId = null)
          const [personalCards, personalGroups] = await Promise.all([
            fetchPromptCards(null),
            fetchPromptGroups(null)
          ]);

          // Fetch workgroup prompts using workgroupId
          const [workgroupCards, workgroupGroups] = await Promise.all([
            fetchPromptCards(workgroupId),
            fetchPromptGroups(workgroupId)
          ]);
          
          console.log('Fetched workgroup cards:', workgroupCards);
          console.log('Fetched workgroup groups:', workgroupGroups);
          
          // Transform personal prompts data
          const personalPromptCards: PromptCard[] = (personalCards as any[]).map(card => ({
            id: card.id || '',
            title: card.title || '',
            description: card.description || '',
            query: card.query || '',
            createdBy: card.createdBy || '',
            createdAt: card.createdAt || new Date(),
            workgroupId: null,
            isPersonal: true
          }));

          const personalPromptGroups: PromptGroup[] = (personalGroups as any[]).map(group => ({
            id: group.id || '',
            name: group.name || '',
            cards: group.cards || [],
            createdBy: group.createdBy || '',
            createdAt: group.createdAt || new Date(),
            workgroupId: null,
            isDefault: group.isDefault || false,
            isPersonal: true
          }));

          // Transform workgroup prompts data
          const workgroupPromptCards: PromptCard[] = (workgroupCards as any[]).map(card => ({
            id: card.id || '',
            title: card.title || '',
            description: card.description || '',
            query: card.query || '',
            createdBy: card.createdBy || '',
            createdAt: card.createdAt || new Date(),
            workgroupId: workgroupId,
            isPersonal: false
          }));

          const workgroupPromptGroups: PromptGroup[] = (workgroupGroups as any[]).map(group => ({
            id: group.id || '',
            name: group.name || '',
            cards: group.cards || [],
            createdBy: group.createdBy || '',
            createdAt: group.createdAt || new Date(),
            workgroupId: workgroupId,
            isDefault: group.isDefault || false,
            isPersonal: false
          }));

          console.log('Transformed workgroup cards:', workgroupPromptCards);
          console.log('Transformed workgroup groups:', workgroupPromptGroups);

          // Combine both sets of prompts
          const allCards = [...personalPromptCards, ...workgroupPromptCards];
          const allGroups = [
            // Add a header group for Personal prompts if there are any
            ...(personalPromptGroups.length > 0 ? [{
              id: 'personal-header',
              name: 'Personal Prompts',
              cards: [],
              createdBy: '',
              createdAt: new Date(),
              workgroupId: null,
              isDefault: false,
              isPersonal: true,
              isHeader: true
            }] : []),
            ...personalPromptGroups,
            // Add a header group for Workgroup prompts if there are any
            ...(workgroupPromptGroups.length > 0 ? [{
              id: 'workgroup-header',
              name: 'Workgroup Prompts',
              cards: [],
              createdBy: '',
              createdAt: new Date(),
              workgroupId: workgroupId,
              isDefault: false,
              isPersonal: false,
              isHeader: true
            }] : []),
            ...workgroupPromptGroups
          ];

          console.log('All cards:', allCards);
          console.log('All groups:', allGroups);

          setPromptCards(allCards);
          setPromptGroups(allGroups);

        } catch (error) {
          console.error('Error loading prompts:', error);
        }
      };
      loadPrompts();
    }
  }, [viewMode, workgroupId, lastRefresh]);

  // Update the prompt card click handler
  const handlePromptClick = (card: PromptCard) => {
    console.log('Prompt card clicked:', card);
    console.log('Calling onPromptSelect with query:', card.query);
    onPromptSelect(card.query);
  };

  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const toggleGroup = (groupId: string) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // Replace window.confirm with custom dialog
  const handleDeleteChat = async (chatId: string) => {
    const confirmed = await confirm('Are you sure you want to delete this chat?', 'delete_chat');
    if (confirmed) {
      try {
        await onDeleteChat(chatId);
        // Rest of your delete logic
      } catch (error) {
        console.error('Error deleting chat:', error);
      }
    }
  };

  return (
    <div className="w-64 border-r dark:border-gray-700 bg-blue-50/50 dark:bg-gray-800 flex flex-col h-full bg-opacity-75 backdrop-blur-sm">

      {/* User profile section removed (moved to TopNavSimple) */}

      {/* View toggle */}
      <div className="flex items-center justify-center p-2 border-b dark:border-gray-700">
        <div className="flex rounded-lg bg-gray-100 dark:bg-gray-700 p-1">
          <button
            onClick={() => setViewMode('prompts')}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              viewMode === 'prompts'
                ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400'
            }`}
          >
            <Plus className="w-4 h-4 inline-block mr-1" />
            Prompts
          </button>
          <button
            onClick={() => setViewMode('conversations')}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              viewMode === 'conversations'
                ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400'
            }`}
          >
            <MessageSquare className="w-4 h-4 inline-block mr-1" />
            Chats
          </button>
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-y-auto p-3 hide-scrollbar">
        {viewMode === 'conversations' ? (
          // Conversations list
          uniqueChats.map((chat, i) => (
            <div
              key={`${chat["session_id"]}-${i}`}
              className="relative"
            >
              <button
                onClick={() => handleSelectChat(chat["session_id"])}
                disabled={deletingChatId === chat["session_id"]}
                className={`w-full text-left p-2 rounded-md transition-colors border border-transparent hover:border-gray-200 dark:hover:border-gray-600 hover:shadow-sm ${
                  selectedChat === chat["session_id"] 
                    ? 'bg-blue-100 dark:bg-gray-700 font-medium' 
                    : 'hover:bg-blue-100/90 dark:hover:bg-gray-700'
                } group relative`}
              >
                <div className="flex items-center gap-2">
                  {loadingChatId === chat["session_id"] ? (
                    <Loader className="w-4 h-4 text-blue-500 dark:text-blue-400 animate-spin" />
                  ) : deletingChatId === chat["session_id"] ? (
                    <Trash className="w-4 h-4 text-red-500 dark:text-red-400" />
                  ) : (
                    <MessageSquare className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  )}
                  <div className="flex-1 overflow-hidden">
                    {renamingId === chat["session_id"] ? (
                      <div onClick={(e) => e.stopPropagation()} className="flex items-center">
                        <input
                          type="text"
                          value={newName}
                          onChange={(e) => setNewName(e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, chat["session_id"])}
                          className="w-full p-1 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded font-normal text-gray-700 dark:text-gray-200"
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Enter name..."
                        />
                        {renamingLoadingId === chat["session_id"] ? (
                          <Loader className="w-4 h-4 text-blue-500 animate-spin ml-1" />
                        ) : (
                          <>
                            <button 
                              onClick={(e) => submitRename(e, chat["session_id"])} 
                              className="p-1" 
                              title="Save (Enter)"
                            >
                              <Check className="w-4 h-4 text-green-500" />
                            </button>
                            <button 
                              onClick={cancelRename} 
                              className="p-1"
                              title="Cancel (Esc)"
                            >
                              <X className="w-4 h-4 text-red-500" />
                            </button>
                          </>
                        )}
                      </div>
                    ) : (
                      <p 
                        className="text-sm font-medium text-gray-700 dark:text-gray-300 line-clamp-1"
                        title={chat["sessionName"] || 'New Chat'}
                      >
                        {chat["sessionName"] || 'New Chat'}
                      </p>
                    )}
                    <p 
                      className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2"
                      title={chat["firstMessage"]}
                    >
                      {chat["firstMessage"]}
                    </p>
                  </div>
                  {!renamingId && (
                    <button 
                      onClick={(e) => handleMenuToggle(e, chat["session_id"])}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 menu-toggle"
                    >
                      <MoreVertical className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    </button>
                  )}
                </div>
              </button>
              
              {menuOpenId === chat["session_id"] && (
                <div 
                  className="absolute right-0 top-10 z-10 bg-white dark:bg-gray-800 shadow-lg rounded-lg p-2 w-48 menu-popup"
                  onMouseLeave={() => setMenuOpenId(null)}
                >
                  <button 
                    onClick={(e) => handleRenameClick(e, chat["session_id"], chat["sessionName"] || 'New Chat')}
                    className="flex items-center gap-2 p-2 w-full hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                  >
                    <Edit className="w-4 h-4" />
                    Rename
                  </button>
                  <button 
                    onClick={(e) => handleDeleteChat(chat["session_id"])}
                    className="flex items-center gap-2 p-2 w-full text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                  >
                    <Trash className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              )}
            </div>
          ))
        ) : (
          // Prompts list grouped by categories
          <div>
            {uniquePromptGroups.map((group) => (
              <div key={`${group.id}-${group.isPersonal ? 'p' : 'w'}-${group.isHeader ? 'h' : 'g'}`} className={`${group.isHeader ? 'mt-6 first:mt-0' : 'mt-4'}`}>
                {group.isHeader ? (
                  // Section headers (Personal/Workgroup Prompts)
                  <div className="mb-3">
                    <button 
                      onClick={() => toggleSection(group.id)}
                      className="flex items-center w-full group"
                    >
                      {collapsedSections[group.id] ? (
                        <ChevronRight className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      )}
                      <h2 className="text-sm font-bold text-gray-900 dark:text-gray-100 tracking-wide uppercase ml-1">
                        {group.name}
                      </h2>
                      <div className="flex-1 ml-3 border-t border-gray-200 dark:border-gray-700"></div>
                    </button>
                  </div>
                ) : (
                  // Regular group content
                  !collapsedSections[group.isPersonal ? 'personal-header' : 'workgroup-header'] && (
                    <>
                      <button 
                        onClick={() => toggleGroup(group.id)}
                        className="flex items-center w-full group px-1 mb-1.5"
                      >
                        {collapsedGroups[group.id] ? (
                          <ChevronRight className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                        ) : (
                          <ChevronDown className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                        )}
                        <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ml-1">
                          {group.name}
                        </h3>
                      </button>
                      {!collapsedGroups[group.id] && (
                        <div className="space-y-1 ml-4">
                          {promptCards
                            .filter(card => {
                              const belongsToGroup = group.cards.includes(card.id);
                              const matchesType = card.isPersonal === group.isPersonal;
                              return belongsToGroup && matchesType;
                            })
                            .map(card => (
                              <button
                                key={`${card.id}-${card.isPersonal ? 'p' : 'w'}`}
                                onClick={() => handlePromptClick(card)}
                                className="w-full text-left p-2 rounded-md hover:bg-blue-100/90 dark:hover:bg-gray-700 transition-colors border border-transparent hover:border-gray-200 dark:hover:border-gray-600 hover:shadow-sm"
                              >
                                <p 
                                  className="text-sm font-medium text-gray-700 dark:text-gray-300 line-clamp-1"
                                  title={card.title}
                                >
                                  {card.title}
                                </p>
                                {card.description && (
                                  <p 
                                    className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2"
                                    title={card.description}
                                  >
                                    {card.description}
                                  </p>
                                )}
                              </button>
                            ))}
                        </div>
                      )}
                    </>
                  )
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
